// src/components/UpgradeModal.js
import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import axiosInstance from '../axiosConfig';
import { AuthContext } from '../AuthContext';
import './EntityModals.css';

function UpgradeModal({ show, handleClose, onSubmit }) {
    const { projects, setProjects, loadingProjects, selectedServiceCenter, authTokens } = useContext(AuthContext);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        photo: null,
        project: '',
    });
    const [submitError, setSubmitError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchProjects = useCallback(async () => {
        if (!selectedServiceCenter || !selectedServiceCenter.id) {
            console.warn('Selected service center is not defined.');
            return;
        }

        try {
            const response = await axiosInstance.get(`/api/projects/?service_center=${selectedServiceCenter.id}`, {
                headers: {
                    'Authorization': `Bearer ${authTokens.access}`
                }
            });
            if (response.status === 200) {
                setProjects(response.data.projects || []); // Обновляем проекты в контексте
            }
        } catch (error) {
            console.error('Failed to fetch projects:', error);
        }
    }, [selectedServiceCenter, authTokens.access, setProjects]);

    useEffect(() => {
        if (show) {
            // Если модальное окно открыто, обновляем проекты
            fetchProjects();
        }
    }, [show, fetchProjects]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleFileChange = (e) => {
        setFormData((prevData) => ({
            ...prevData,
            photo: e.target.files[0],
        }));
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');
        setIsSubmitting(true);

        const data = new FormData();
        for (const key in formData) {
            if (formData[key] !== null) {
                data.append(key, formData[key]);
            }
        }

        try {
            const response = await axiosInstance.post('/api/create_upgrade/', data);
            if (!response.data.success) {
                throw new Error(response.data.errors || 'Не удалось создать апгрейд.');
            }

            onSubmit(response.data);
            handleClose();
        } catch (error) {
            const responseData = error?.response?.data;
            const message = responseData?.errors
                || responseData?.detail
                || error?.message
                || 'Ошибка при создании апгрейда.';
            setSubmitError(typeof message === 'string' ? message : JSON.stringify(message));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            className="entity-modal"
            dialogClassName="entity-modal-dialog entity-modal-dialog--sm"
            contentClassName="entity-modal-content"
        >
            <Modal.Header closeButton>
                <Modal.Title>Добавить апгрейд</Modal.Title>
            </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleFormSubmit} className="entity-form">
                    {submitError && <Alert variant="danger">{submitError}</Alert>}
                    <Form.Group controlId="formName" className="entity-field">
                        <Form.Label className="entity-label">Название</Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                        />
                    </Form.Group>
                    <Form.Group controlId="formDescription" className="entity-field">
                        <Form.Label className="entity-label">Описание</Form.Label>
                        <Form.Control
                            as="textarea"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <Form.Group controlId="formPhoto" className="entity-field">
                        <Form.Label className="entity-label">Фото</Form.Label>
                        <Form.Control
                            type="file"
                            name="photo"
                            accept="image/*"
                            onChange={handleFileChange}
                        />
                    </Form.Group>
                    <Form.Group controlId="formProject" className="entity-field">
                        <Form.Label className="entity-label">Проект</Form.Label>
                        <Form.Control
                            as="select"
                            name="project"
                            value={formData.project}
                            onChange={handleChange}
                            required
                            disabled={loadingProjects}
                        >
                            <option value="">Выберите проект</option>
                            {projects.map(project => (
                                <option key={project.id} value={project.id}>
                                    {project.name}
                                </option>
                            ))}
                        </Form.Control>
                    </Form.Group>
                    <div className="entity-actions">
                        <Button className="entity-btn entity-btn-primary" type="submit" disabled={isSubmitting}>
                            Сохранить
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}

export default UpgradeModal;
